import React from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Trophy, Play } from "lucide-react";
import { RoyalStage } from "./RoyalStage";
import { ResultDigits } from "./ResultDigits";
import Countdown from "./Countdown";
import { StoryButton } from "./StoryButton";
import { slotLabel } from "../lib/slotVibe";
import { tx } from "../lib/miniI18n";
import { useI18n } from "../i18n";

/* Category page: each show as the same royal stage the customer sees on the booking page (name · last result · CLOSE / RESULT / CLOSES IN). Tap → book. */
export const ShowStageCard = ({ game, onClosed, image }) => {
  const navigate = useNavigate();
  const { lang } = useI18n();
  const rt = game.runtime || {};
  const interval = rt.mode === "interval";
  const drawTime = interval ? (rt.draw_slot || game.draw_time) : game.draw_time;
  const open = !!rt.is_open;
  return (
    <div onClick={() => navigate(`/play/${game.id}`)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && navigate(`/play/${game.id}`)} className="cursor-pointer active:scale-[0.99] transition-transform" data-testid={`show-stage-${game.id}`}>
      <RoyalStage game={game} image={image || game.logo} drawTime={drawTime} className="rounded-2xl p-4 shadow-lg">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display font-black text-white text-xl leading-tight">{game.name}</h3>
            <span className="text-[10px] uppercase tracking-widest text-blue-100">{game.category}</span>
          </div>
          {game.last_result ? (
            <div className="text-right" onClick={(e) => e.stopPropagation()}>
              <div className="text-[9px] text-blue-100 flex items-center gap-1 justify-end mb-1"><Trophy className="w-3 h-3" />{tx("Last Result")}</div>
              <ResultDigits number={game.last_result.winning_number} size="w-7 h-7 text-xs" />
              {!interval && <div className="mt-1 flex justify-end"><StoryButton resultId={game.last_result.id} compact /></div>}
            </div>
          ) : (
            <div className="text-[10px] text-blue-100 bg-white/10 rounded-full px-2 py-1" data-testid={`show-stage-waiting-${game.id}`}>{lang === "ta" ? "Result வரவில்லை" : "Result awaited"}</div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 mt-4 text-center">
          <div className="bg-white/15 rounded-xl py-2"><div className="text-[9px] text-blue-100">{tx("CLOSE")}</div><div className="text-sm font-bold">{interval ? (rt.close_slot || "—") : game.close_time}</div></div>
          <div className="bg-white/15 rounded-xl py-2"><div className="text-[9px] text-blue-100">{interval ? "NEXT DRAW" : "RESULT"}</div><div className="text-sm font-bold">{interval ? (rt.draw_slot || "—") : game.draw_time}</div></div>
          <div className="bg-white/15 rounded-xl py-2"><div className="text-[9px] text-blue-100 flex items-center gap-0.5 justify-center"><Clock className="w-2.5 h-2.5" />{open ? tx("CLOSES IN") : "CLOSED"}</div><div className="text-sm font-bold">{open ? <Countdown seconds={rt.closing_seconds} className="text-amber-300" onExpire={onClosed} /> : <span className="text-white/60">—</span>}</div></div>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div className="text-[9px] tracking-widest text-white/60">✦ {slotLabel(drawTime, lang)} ✦</div>
          <span className={`inline-flex items-center gap-1 text-[11px] font-black px-3 py-1 rounded-full ${open ? "rl-gold-bg" : "bg-white/15 text-white/70"}`} data-testid={`show-stage-play-${game.id}`}><Play className="w-3 h-3" fill="currentColor" />{open ? (lang === "ta" ? "BOOK" : "PLAY") : "VIEW"}</span>
        </div>
      </RoyalStage>
    </div>
  );
};
